<!--
  @Equipment  Button "Ausrüstung" which opens the equipment dialog (Classical parchment design):
              figure in the middle, equipped items arranged around it, switchable between armour and jewellery;
              the equipment set (outfit) is chosen top right.
-->

<script>
	import {user, view, send_data, change_attribute} from "../client.svelte.js"

	/** slot arrangement around the figure: [slot, grid row, label] **/
	const layouts = {
		weapons: {
			left:  [['mainhand', 1, "Haupthand"], ['back', 2, "Rücken"], ['belt_1', 4, "Waffengurt rechts"], ['boot', 5, "Stiefel"]],
			right: [['sidehand', 1, "Nebenhand"], ['baldric', 2, "Schultergehänge"], ['sleeve', 3, "Ärmel"],['belt_2', 4, "Waffengurt links"]]},
		armor: {
			left:  [['corearmor', 1, "Rüstung"], ['torso', 2, "Torso"], ['hands', 3, "Hände"], ['waist', 4, "Hüfte"], ['feet', 6, "Füße"]],
			right: [['head', 1, "Kopf"], ['shoulders', 2, "Schultern"], ['arms', 3, "Arme"], ['legs', 5, "Beine"]]},
		jewelry: {
			left:  [['ears', 1, "Ohren"], ['wrists', 3, "Handgelenk"], ['finger_1', 4, "rechte Hand"]],
			right: [['neck', 1, "Hals"], ['cloak', 2, "Umhang"], ['finger_2', 4, "linke Hand"]]}}
	const slotFilters =
				{mainhand: '012', sidehand: '+', back: '01+', belt_1: '01+', belt_2: '01+', baldric: '0', sleeve: '0', boot: '0'}
	const tabs = [{id: 'weapons', label: "Waffen"}, {id: 'armor', label: "Rüstung"}, {id: 'jewelry', label: "Schmuck"}]
	const newSetOption = "_new"

	let dialog
	let tab = $state('armor')
	let viewOutfit = $state(null)
	let addingSet = $state(false)		// shows the name input for a new equipment set
	let newItem = $state("")

	let outfits = $derived(
		user.equipments.values()
			.filter(equip => equip.CHAR === view.character?.ID)
			.map(equip => ({ID: equip.ID, name: equip.OUTFIT}))
			.toArray() )
	let equipment = $derived(user.equipments.get(viewOutfit))
	// $inspect(`EQUIPMENT - current equipment`, equipment)

	/** items the hero possesses (without coins), usable as options for the slots **/
	let items = $derived(
		(view.character?.possessions ?? [])
			.map(possID => user.possessions.get(possID))
			.filter(poss => poss && poss.gained !== null)		// without items only marked for trade
			.map(poss => user.gearitems.get(poss.ITEM))
			.filter(item => item && !['gold', 'silver'].includes(item.ID) && !Object.values(equipment).includes(item.ID))
			.sort((a, b) => a.name.localeCompare(b.name)) )


	// keep the chosen set as long as it exists, otherwise fall back to the first one
	$effect(() => {
		if (!outfits.some(outfit => outfit.ID === viewOutfit))
			viewOutfit = outfits[0]?.ID ?? null
	})

	function item_name(item) {
		return (item.specials) ? `${item.name} (${item.specials})` : item.name
	}

	/** weapons only in the hands, armour only as core armour, otherwise only items fitting the slot **/
	function slot_options(slot) {
		const baseSlot = slot.split('_')[0]
		let fitting
		if (slot in slotFilters) {
			const filterSet = new Set(slotFilters[slot])
			fitting = items.filter(item => {
				const wieldSet = new Set(item.wield)
				return !wieldSet.isDisjointFrom(filterSet)
			})
		} else fitting =
			// : (slot === 'mainhand' || slot === 'sidehand') ? items.filter(item => (item.ZUB ?? item.TYPE) === 'Weapon')
			(slot === 'corearmor') ? items.filter(item => (item.ZUB ?? item.TYPE) === 'Armor') : items.filter(item => item.slot === baseSlot)
		const equippedID = equipment?.[slot]
		if (!equippedID || fitting.some(item => item.ID === equippedID))
			return fitting
		// equipped but not among the hero's items, e.g. the server's standard armour 'OR'
		const equipped = user.gearitems.get(equippedID) ?? {ID: equippedID, name: (equippedID === 'OR') ? "ohne Rüstung" : equippedID}
		return [equipped, ...fitting]
	}

	function equip_item(slot, itemID) {
		console.log(`EQUIPMENT - equip ${itemID || "nothing"} at ${slot} of ${viewOutfit}`)
		change_attribute.bind(equipment)({target: {id: slot, value: itemID}})
	}

	function select_set(e) {
		if (e.target.value === newSetOption) {
			addingSet = true
			e.target.value = viewOutfit ?? ""
		} else
			viewOutfit = e.target.value
	}

	function check_key(e) {
	  if (e.key === 'Escape') {
		  e.preventDefault()		// keeps the dialog open
		  addingSet = false
	  }
	  // if (e.key === 'Enter')
		//   add_set(e)
  }

	async function add_set(e) {
		const name = e.target.value.trim()
		addingSet = false
		if (!name || outfits.some(outfit => outfit.name === name))  return
		console.log(`EQUIPMENT - add new equipment set ${name}`)
		await send_data('/equipment/_', {CHAR: view.character.ID, OUTFIT: name}, 'POST')
		viewOutfit = outfits.find(outfit => outfit.name === name)?.ID ?? viewOutfit
	}

	function acquire_item() {
		if (!newItem) {
			alert("Du musst erst einen Gegenstand angeben!")
			return
		}
		send_data('/possession/_', {CHAR: view.character.ID, ITEM: newItem, amount: 1}, 'POST')
		newItem = ""
	}

</script>


<!-------------------------------- HTML -------------------------------------->

<dialog bind:this={dialog} id='equip-dialog' data-help="equipment" class="sheet-dialog classical paper-aged" aria-label="Ausrüstung">
	<header class="equip-head">
		<div>
			<div class="kicker">{view.character?.name}</div>
			<h2 class="equip-title">Ausrüstung</h2>
		</div>
		<div class="head-right">
			<label class="set" data-help="outfit">
				<span class="kicker">Set</span>
				{#if addingSet}
					<input class="set-input" placeholder="Name des Sets" autofocus onkeydown={check_key} onchange={add_set}/>
				{:else}
					<select class="set-select" value={viewOutfit ?? ""} onchange={select_set}>
						{#each outfits as outfit}
							<option value={outfit.ID}>{outfit.name}</option>
						{:else}
							<option value="">—</option>
						{/each}
						<option value={newSetOption}>Neues Set …</option>
					</select>
				{/if}
			</label>
			<button class="close fa icon-xmark" title="Schließen" aria-label="Schließen" onclick={() => dialog.close()}></button>
		</div>
	</header>
	<div class="double-rule"></div>

	<nav class="equip-tabs">
		{#each tabs as t}
			<button class="equip-tab" data-help={t.id} class:active={tab === t.id} onclick={() => tab = t.id}>{t.label}</button>
		{/each}
	</nav>

	{#if equipment}
		<div class="figure-grid" data-help={tab}>
			<div class="figure">
				<img class="figure-img" src="/static/images/body.png" alt="Figur"/>
			</div>
			{#each layouts[tab].left as [slot, row, slotLabel]}
				{@render slotfield(slot, row, slotLabel, 'left')}
			{/each}
			{#each layouts[tab].right as [slot, row, slotLabel]}
				{@render slotfield(slot, row, slotLabel, 'right')}
			{/each}
		</div>
	{:else}
		<p class="muted no-set">Noch kein Ausrüstungs-Set vorhanden – über „Set“ ein neues anlegen.</p>
	{/if}

	<footer class="equip-foot">
		<button class="btn-outline done" onclick={() => dialog.close()}>Fertig</button>
	</footer>
</dialog>


{#snippet slotfield(slot, row, slotLabel, side)}
	<div class="slot {side} row{row}">
		<label class="slot-field">
			<span class="kicker">{slotLabel}</span>
			<select class="slot-select" value={equipment[slot] ?? ""} onchange={(e) => equip_item(slot, e.target.value)}>
				<option value="">—</option>
				{#each slot_options(slot) as item}
					<option value={item.ID}>{item_name(item)}</option>
				{/each}
			</select>
		</label>
		<span class="connector"></span>
	</div>
{/snippet}


<!-------------------------------- STYLE -------------------------------------->

<style lang="sass">
	#equip-dialog
		width: min(1000px, calc(100vw - 48px))
		max-height: calc(100vh - 48px)
		padding: 32px 48px 36px
		border: 1px solid var(--color-accent-300)
		border-radius: var(--radius-lg)
		box-shadow: 0 20px 48px rgba(0, 0, 0, .6)
		overflow: auto

		&::backdrop
			background: rgba(0, 0, 0, .65)

	/* header */
	.equip-head
		display: flex
		align-items: flex-end
		justify-content: space-between
		flex-wrap: wrap
		gap: 24px

	.equip-title
		margin-top: 2px
		font-weight: 400
		font-size: 40px
		line-height: 1

	.head-right
		display: flex
		align-items: flex-end
		gap: 28px

	.set
		display: flex
		flex-direction: column
		gap: 2px

	.set-select, .set-input
		min-width: 180px
		padding: 4px 0
		border: none
		border-bottom: 1px solid var(--color-neutral-400)
		background: transparent
		font-family: var(--font-body)
		font-size: 15px
		color: var(--color-text)
		outline: none

		&:focus
			border-bottom-color: var(--color-accent)

	.close
		align-self: flex-start
		width: 36px
		height: 36px
		padding: 0
		border: none
		border-radius: var(--radius-md)
		background: none
		color: var(--color-accent)
		font-size: 18px
		cursor: pointer

		&:hover
			background: color-mix(in srgb, var(--color-accent) 10%, transparent)

	.double-rule
		height: 4px
		margin: 16px 0 20px
		border-top: 1px solid var(--color-accent)
		border-bottom: 1px solid var(--color-accent-300)

	/* Rüstung / Schmuck */
	.equip-tabs
		display: flex
		justify-content: center
		gap: 28px
		margin-bottom: 20px

	.equip-tab
		padding: 4px 2px
		border: none
		border-bottom: 1px solid transparent
		background: none
		font-family: var(--font-heading)
		font-weight: 600
		font-size: 15px
		letter-spacing: .12em
		text-transform: uppercase
		color: var(--color-neutral-700)
		cursor: pointer

		&:hover
			color: var(--color-text)
			border-bottom-color: var(--color-accent-300)

		&.active
			border-bottom-color: var(--color-accent)
			color: var(--color-accent-800)
			cursor: default

	/* figure with the slots arranged around it */
	.figure-grid
		display: grid
		grid-template-columns: minmax(0, 1fr) 280px minmax(0, 1fr)
		grid-template-rows: repeat(6, 76px)
		column-gap: 8px

	.figure
		grid-column: 2
		grid-row: 1 / -1
		display: flex
		justify-content: center
		border: 6px solid var(--color-surface)
		outline: 1px solid var(--color-divider)
		background: var(--color-surface)

	.figure-img
		height: 100%
		object-fit: contain

	.slot
		align-self: center
		display: flex
		align-items: flex-end
		gap: 10px

		&.left
			grid-column: 1

		&.right
			grid-column: 3
			flex-direction: row-reverse

		@for $row from 1 through 6
			&.row#{$row}
				grid-row: $row

	.slot-field
		flex: 1
		min-width: 0
		display: flex
		flex-direction: column
		gap: 2px

		.left &
			text-align: right

	.slot-select
		width: 100%
		padding: 4px 0
		border: none
		border-bottom: 1px solid var(--color-neutral-400)
		background: transparent
		font-family: var(--font-body)
		font-size: 15px
		color: var(--color-text)
		outline: none
		cursor: pointer

		.left &
			text-align: right
			text-align-last: right

		&:focus
			background: var(--focus-tint)
			border-bottom-color: var(--color-accent)

	// hairline from the field towards the figure, ending in a small ring
	.connector
		position: relative
		flex: none
		width: 44px
		height: 1px
		margin-bottom: 10px
		background: var(--color-accent-300)

		&::after
			content: ""
			position: absolute
			top: -3px
			width: 7px
			height: 7px
			border: 1px solid var(--color-accent)
			border-radius: 50%

		.left &::after
			right: -3px

		.right &::after
			left: -3px

	.no-set
		margin: 24px 0
		text-align: center

	/* footer: coins, new item, done */
	.equip-foot
		display: flex
		align-items: flex-end
		flex-wrap: wrap
		gap: 16px
		margin-top: 28px
		padding-top: 18px
		border-top: 1px solid var(--color-divider)

	.done
		margin-left: auto
</style>
